import { DOCUMENT } from '@angular/common'
import { ChangeDetectorRef, Component, Inject, NgZone, OnDestroy } from '@angular/core'
import { EmployerNavigationService } from '@employer/app/services/navigation.service'
import { combineLatest, map, Observable, of, startWith, Subscription, switchMap } from 'rxjs'
import { GuideService, IGuideActiveStep, IGuideStep } from '../../services/guide.service'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { findTourAnchor } from '../../utils/tour-anchor.util'
import { buildNavPeek, FAVORITES_PEEK_ROW, ITourNavPeek, ITourPeekRow, toPeekRow } from '../../utils/tour-nav-peek.util'
import {
  ARROW_SIZE,
  ArrowPosition,
  ArrowSide,
  computeArrowPosition,
  computePeekPosition,
  PEEK_CARET,
  PEEK_WIDTH,
  PeekPosition,
  Rect,
} from '../../utils/tour-position.util'
import { HAMBURGER_ANCHOR, resolveTourTarget } from '../../utils/tour-target.util'

/** The measured indicator for the current step: where to ring, and what to hang off the ring. */
export interface ITourIndicatorView {
  rect: Rect
  /** The floating arrow, or `null` when the peek bubble stands in for it (its caret does the pointing). */
  arrow: ArrowPosition | null
  /** The drawer peek, shown only when the step has retargeted to the hamburger. */
  peek: { rows: ITourNavPeek; position: PeekPosition } | null
}

/**
 * The tour's indicator layer (CNCT-6771) — the highlight ring and the floating arrow that travel to
 * whatever the current step is about, while the narration card stays parked in its corner.
 *
 * design-contract.md → §Surface 3.1 (accent ring, `data-tour-id` anchors, inert backdrop) and
 * §Surface 3.2 (narration placement is fixed; indicator placement is anchored).
 *
 * The page is NOT dimmed: the ring and arrow alone mark the subject, and the rest of the screen stays
 * at full brightness so the user can still read the area the step is describing.
 *
 * The indicators are drawn in Cnect pink (`skin-pink` / `--color-pink`) rather than
 * `skin-primary-accent`. The accent is tenant-themable and resolves to blue in the default theme, which
 * left the indicators reading as ordinary UI chrome; the tour wants one fixed, unmistakable
 * "this is the tour talking" colour across tenants.
 *
 * Where the left-nav rail is hidden and the step has retargeted to the header hamburger, the arrow is
 * replaced by a **drawer peek** — a slice of the menu in the rail's own styling, with the step's row
 * ringed and its real neighbours faded either side — so the user knows which row to look for before
 * opening the menu. The bubble's caret does the pointing there.
 *
 * Two fixed layers, deliberately straddling the card's `z-1001`:
 *   - the capture shield (`z-1000`, under the card) is fully transparent. It captures pointer events so
 *     the page beneath is inert during a step, and carries NO click handler, so clicking it neither
 *     advances nor closes the tour — Back/Next/close are the only ways through.
 *   - the ring + arrow (`z-1002`, above the card) are `pointer-events-none`. Sitting above the card
 *     means a subject that happens to fall behind it is still visibly ringed, and the card's own
 *     controls keep working.
 *
 * The rect is re-measured on an animation frame while a step is showing and pushed only when it
 * actually changes. One loop covers everything that can move the subject — a nav group expanding, the
 * account dropdown opening, a scroll, a mid-tour resize between wide and narrow (which also re-resolves
 * the target itself, so the ring follows the rail behind the hamburger rather than being stranded over
 * empty space). A subject with no layout box renders nothing at all rather than a zero-size ring.
 */
@Component({
  selector: 'tour-indicator',
  template: `
    <ng-container *ngIf="view as v">
      <!-- Transparent capture shield. The page reads at full brightness — nothing is dimmed — but the
           shield keeps it inert during a step, and carries no click handler, so a click on it neither
           advances nor closes the tour. Under the card, so the card's controls stay live. -->
      <div class="e11-fixed e11-inset-0 e11-z-[1000]" data-tour-indicator-shield></div>

      <!-- Ring + arrow, above the card so they are never occluded by it. -->
      <div class="e11-fixed e11-inset-0 e11-z-[1002] e11-pointer-events-none" data-tour-indicator>
        <div
          class="tour-indicator-box e11-absolute e11-rounded-md e11-ring-4 e11-ring-skin-pink"
          data-tour-indicator-ring
          [style.top.px]="v.rect.top"
          [style.left.px]="v.rect.left"
          [style.width.px]="v.rect.width"
          [style.height.px]="v.rect.height"
        ></div>
        <span
          *ngIf="v.arrow as arrow"
          class="tour-indicator-arrow material-icons-outlined e11-absolute e11-text-skin-pink"
          data-tour-indicator-arrow
          [attr.data-tour-indicator-arrow-side]="arrow.side"
          aria-hidden="true"
          [style.top.px]="arrow.top"
          [style.left.px]="arrow.left"
          [style.fontSize.px]="arrowSize"
          >{{ arrowIcon(arrow.side) }}</span
        >

        <!-- Drawer peek: a slice of the menu hanging under the hamburger, so the user knows which row
             to look for before opening it. Renders in the rail's own dark treatment and row styling,
             with the step's row ringed and its real neighbours faded either side. Its caret does the
             pointing, which is why the arrow stands down whenever this is showing. -->
        <div
          *ngIf="v.peek as peek"
          class="tour-peek e11-absolute e11-rounded-lg e11-bg-skin-app-bg-dark e11-p-1.5 e11-shadow-2xl"
          data-tour-indicator-peek
          aria-hidden="true"
          [style.top.px]="peek.position.top"
          [style.left.px]="peek.position.left"
          [style.width.px]="peekWidth"
        >
          <span class="tour-peek-caret e11-absolute e11-bg-skin-app-bg-dark" [style.left.px]="peek.position.caretLeft"></span>
          <div class="tour-peek-cap e11-px-1.5 e11-pt-0.5 e11-pb-1.5">{{ 'In the main menu' | translate }}</div>
          <ng-container *ngTemplateOutlet="peekRow; context: { row: peek.rows.above, target: false }"></ng-container>
          <ng-container *ngTemplateOutlet="peekRow; context: { row: peek.rows.target, target: true }"></ng-container>
          <ng-container *ngTemplateOutlet="peekRow; context: { row: peek.rows.below, target: false }"></ng-container>
        </div>
      </div>
    </ng-container>

    <!-- One peek row, mirroring app-nav-item's top-level row: icon, label, chevron on a group. An empty
         slot at either end of the menu keeps the target row's vertical position steady between steps. -->
    <ng-template #peekRow let-row="row" let-target="target">
      <div
        class="tour-peek-row e11-flex e11-items-center e11-gap-2 e11-px-2 e11-rounded-lg"
        [class.tour-peek-target]="target"
        [class.tour-peek-ghost]="!target"
      >
        <ng-container *ngIf="row">
          <span *ngIf="row.icon" class="material-symbols-outlined md-18">{{ row.icon }}</span>
          <span class="e11-flex-1 e11-truncate">{{ row.label | translate }}</span>
          <span *ngIf="row.hasChildren" class="material-symbols-outlined tour-peek-chevron">expand_more</span>
        </ng-container>
      </div>
    </ng-template>
  `,
  styles: [
    `
      /* The ring, cutout and arrow slide to the next subject rather than blinking out and back in. */
      .tour-indicator-box,
      .tour-indicator-arrow {
        transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease;
      }
      .tour-indicator-arrow {
        line-height: 1;
        /* Direction-agnostic so one keyframe serves all four arrow sides. */
        animation: tour-indicator-arrow-pulse 1.4s ease-in-out infinite;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
      }
      @keyframes tour-indicator-arrow-pulse {
        0%,
        100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.6;
          transform: scale(1.18);
        }
      }
      /* Reduced motion: the indicators still land in the right place, they just stop moving and pulsing. */
      /* Peek bubble — the rail's own treatment, so the previewed row reads as the row it previews.
         Values mirror app-nav-item's top-level rows (65%-white label, 10%-white active wash, 8px
         radius, 18px outlined icon). */
      .tour-peek {
        transition: top 0.25s ease, left 0.25s ease;
      }
      .tour-peek-caret {
        top: -5px;
        width: 12px;
        height: 12px;
        transform: rotate(45deg);
        border-radius: 2px;
      }
      .tour-peek-cap {
        font-size: 9px;
        line-height: 1;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.42);
      }
      .tour-peek-row {
        height: 34px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.65);
      }
      .tour-peek-ghost {
        opacity: 0.3;
      }
      .tour-peek-target {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        font-weight: 500;
        /* Ringed in the same pink as the hamburger outline, so the previewed row and the thing the
           user is being pointed at read as one indication. */
        box-shadow: 0 0 0 2px rgb(var(--color-pink));
      }
      .tour-peek-chevron {
        font-size: 17px;
        opacity: 0.55;
      }

      @media (prefers-reduced-motion: reduce) {
        .tour-indicator-box,
        .tour-indicator-arrow,
        .tour-peek {
          transition: none;
          animation: none;
        }
      }
    `,
  ],
})
export class TourIndicatorComponent implements OnDestroy {
  readonly arrowSize = ARROW_SIZE
  readonly peekWidth = PEEK_WIDTH
  readonly peekCaret = PEEK_CARET

  /** The measured indicator, or `null` when the current step shows none (the intro, or an absent subject). */
  view: ITourIndicatorView | null = null

  /** Glyph per arrow side — the arrow sits on that side and points back at the subject. */
  private static readonly ARROW_ICONS: Record<ArrowSide, string> = {
    right: 'arrow_back',
    left: 'arrow_forward',
    bottom: 'arrow_upward',
    top: 'arrow_downward',
  }

  private step: IGuideStep | null = null
  private frame = 0
  private readonly subs = new Subscription()

  /**
   * The top-level nav rows this user can actually see, in menu order — the peek's source of truth.
   *
   * `isVisible` is mirrored exactly as `app-nav-item` applies it: an observable that has not emitted yet
   * counts as hidden (the async pipe yields `null` there and the row's `*ngIf` fails), which is what
   * `startWith(false)` encodes. It also means one never-emitting gate cannot stall the whole list.
   */
  private navRows: ITourPeekRow[] = []

  /** Whether the menu will show a Favorites section — see `peekRows`. */
  private hasFavorites = false

  constructor(
    private guide: GuideService,
    private navigation: EmployerNavigationService,
    private navFavorites: NavFavoritesService,
    @Inject(DOCUMENT) private document: Document,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {
    this.subs.add(this.guide.currentStep$.subscribe(active => this.onStep(active)))
    this.subs.add(this.visibleNavRows$().subscribe(rows => (this.navRows = rows)))
    this.subs.add(this.navFavorites.favorites$.subscribe(ids => (this.hasFavorites = ids.length > 0)))
  }

  private visibleNavRows$(): Observable<ITourPeekRow[]> {
    return this.navigation.navigationItems$.pipe(
      switchMap(items =>
        items?.length
          ? combineLatest(items.map(item => (item.isVisible ? item.isVisible.pipe(startWith(false)) : of(true)))).pipe(
              map(visible => items.filter((_, i) => visible[i]))
            )
          : of([])
      ),
      map(items => items.map(toPeekRow))
    )
  }

  ngOnDestroy(): void {
    this.stopTracking()
    this.subs.unsubscribe()
  }

  arrowIcon(side: ArrowSide): string {
    return TourIndicatorComponent.ARROW_ICONS[side]
  }

  /** Start tracking a step that names a subject; clear the layer for one that does not (the intro). */
  private onStep(active: IGuideActiveStep | null): void {
    this.step = active?.step ?? null
    if (!this.step?.anchor) {
      this.stopTracking()
      this.setView(null)
      return
    }
    this.measure()
    this.startTracking()
  }

  /**
   * Re-measure every frame, outside Angular so an idle tour costs no change detection. Cheap: one
   * `querySelectorAll` + one `getBoundingClientRect`, and a push into Angular only when the rounded
   * geometry actually changes.
   */
  private startTracking(): void {
    if (this.frame) return
    this.zone.runOutsideAngular(() => {
      const tick = () => {
        this.frame = requestAnimationFrame(tick)
        this.measure()
      }
      this.frame = requestAnimationFrame(tick)
    })
  }

  private stopTracking(): void {
    if (!this.frame) return
    cancelAnimationFrame(this.frame)
    this.frame = 0
  }

  private measure(): void {
    const step = this.step
    if (!step) return
    const viewport = { width: this.viewportWidth(), height: this.viewportHeight() }
    // Re-resolved every frame, not once per step: a mid-tour resize past the `lg` breakpoint moves the
    // subject from the left-nav rail to the header hamburger, and the ring must follow it.
    const target = resolveTourTarget(step, viewport.width)
    const el = target.anchor ? findTourAnchor(this.document, target.anchor) : null
    const domRect = el?.getBoundingClientRect()
    const laidOut = !!domRect && (domRect.width > 0 || domRect.height > 0)
    if (!laidOut) {
      this.setView(null)
      return
    }
    const rect: Rect = {
      top: Math.round(domRect!.top),
      left: Math.round(domRect!.left),
      width: Math.round(domRect!.width),
      height: Math.round(domRect!.height),
    }

    // On the hamburger the ring alone cannot say WHICH row to look for, so the peek does — and its own
    // caret points at the hamburger, so the arrow stands down rather than competing with it. If the
    // step's row is not among the rows this user can see, there is nothing honest to preview: fall back
    // to the arrow rather than naming a row that is not in their menu.
    const peekRows = target.anchor === HAMBURGER_ANCHOR ? buildNavPeek(this.peekRows(), this.peekTourId(step)) : null
    if (peekRows) {
      this.setView({ rect, arrow: null, peek: { rows: peekRows, position: computePeekPosition(rect, viewport) } })
      return
    }
    this.setView({ rect, arrow: computeArrowPosition(rect, viewport, target.arrow), peek: null })
  }

  /** The nav row this step is about — its own anchor, never the hamburger it retargeted to. */
  private peekTourId(step: IGuideStep): string {
    return step.navFocus ?? step.anchor ?? ''
  }

  /**
   * The menu as the peek previews it: the nav rows, preceded by Favorites when the user has starred
   * something. Favorites is its own component rather than a nav config item, so it has no entry to
   * map from.
   *
   * Read from the favourites state, NOT the DOM. This peek previews what the MOBILE DRAWER will show
   * when the user taps the hamburger, and that drawer is not rendered until it opens — so the only
   * copy of the menu in the document is the desktop rail, which is hidden at these widths and, with
   * an extended-nav panel open, renders icon-only and has no Favorites section at all. Looking there
   * asked the wrong surface and came back empty exactly when a Favorites step needed it. `navRows`
   * already comes from the nav config for the same reason.
   */
  private peekRows(): ITourPeekRow[] {
    return this.hasFavorites ? [FAVORITES_PEEK_ROW, ...this.navRows] : this.navRows
  }

  /** Push into Angular only on a real change, so the rAF loop does not thrash change detection. */
  private setView(next: ITourIndicatorView | null): void {
    if (sameView(this.view, next)) return
    this.zone.run(() => {
      this.view = next
      this.cdr.detectChanges()
    })
  }

  private viewportWidth(): number {
    return this.document.defaultView?.innerWidth ?? 1440
  }

  private viewportHeight(): number {
    return this.document.defaultView?.innerHeight ?? 900
  }
}

/** Geometry equality for the rAF loop — whole-px, so sub-pixel jitter does not re-render. */
function sameView(a: ITourIndicatorView | null, b: ITourIndicatorView | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.rect.top === b.rect.top &&
    a.rect.left === b.rect.left &&
    a.rect.width === b.rect.width &&
    a.rect.height === b.rect.height &&
    sameArrow(a.arrow, b.arrow) &&
    samePeek(a.peek, b.peek)
  )
}

function sameArrow(a: ArrowPosition | null, b: ArrowPosition | null): boolean {
  if (!a || !b) return a === b
  return a.side === b.side && Math.round(a.top) === Math.round(b.top) && Math.round(a.left) === Math.round(b.left)
}

function samePeek(a: ITourIndicatorView['peek'], b: ITourIndicatorView['peek']): boolean {
  if (!a || !b) return a === b
  return (
    a.rows.target.tourId === b.rows.target.tourId &&
    a.rows.above?.tourId === b.rows.above?.tourId &&
    a.rows.below?.tourId === b.rows.below?.tourId &&
    Math.round(a.position.top) === Math.round(b.position.top) &&
    Math.round(a.position.left) === Math.round(b.position.left) &&
    Math.round(a.position.caretLeft) === Math.round(b.position.caretLeft)
  )
}
